import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getAdminId } from '../utils/getAdminId'
import { useRfidIdentifiers } from './useRfidIdentifiers'
import { ThyWebSocketEncoderClient } from '../lib/rfid/thy-websocket-encoder-client'
import type {
  EncoderConnectionStatus,
  EncoderDeviceInfo,
  EncoderFrameLogEntry,
  UhfEncoderClient,
} from '../lib/rfid/uhf-encoder-client'
import {
  classifyTag,
  encodeTag,
  listStalePendingEncodes,
  preReadTag,
  type EncodeClassification,
  type EncodeResult,
  type RfidUnitRecord,
  type TagPreRead,
} from '../lib/services/rfidEncodeService'
import type { RfidMatrixRow } from '../types/rfid'

/** Above this the field reaches well past the pad and can catch the wrong garment. */
export const RF_POWER_WARNING_THRESHOLD = 8

interface BlockedState {
  classification: Extract<EncodeClassification, { outcome: 'blocked' }>
}

export function useRfidEncoder() {
  const { user } = useAuth()
  const adminId = getAdminId(user)
  const { scheme, groups, isLoading: isConfigLoading, error: configError } = useRfidIdentifiers()

  const clientRef = useRef<UhfEncoderClient | null>(null)
  if (!clientRef.current) clientRef.current = new ThyWebSocketEncoderClient()
  const client = clientRef.current

  const [status, setStatus] = useState<EncoderConnectionStatus>(client.getStatus())
  const [deviceInfo, setDeviceInfo] = useState<EncoderDeviceInfo | null>(null)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)

  const [selectedProductName, setSelectedProductName] = useState<string | null>(null)
  const [selectedColorName, setSelectedColorName] = useState<string | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<RfidMatrixRow | null>(null)

  const [isEncoding, setIsEncoding] = useState(false)
  const [encodeError, setEncodeError] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<BlockedState | null>(null)
  const [lastResult, setLastResult] = useState<EncodeResult | null>(null)
  const [lastPreRead, setLastPreRead] = useState<TagPreRead | null>(null)
  const [stalePending, setStalePending] = useState<RfidUnitRecord[]>([])

  useEffect(() => client.onStatusChange(setStatus), [client])

  useEffect(() => {
    return () => {
      void client.disconnect()
    }
  }, [client])

  const refreshStalePending = useCallback(async () => {
    if (!adminId) return
    try {
      setStalePending(await listStalePendingEncodes(adminId))
    } catch {
      // A failure here must never block encoding; the panel simply stays empty.
    }
  }, [adminId])

  useEffect(() => {
    void refreshStalePending()
  }, [refreshStalePending])

  const connect = useCallback(async () => {
    setConnectionError(null)
    setIsConnecting(true)
    try {
      setDeviceInfo(await client.connect())
    } catch (err) {
      setDeviceInfo(null)
      setConnectionError(err instanceof Error ? err.message : 'Failed to reach the RFID bridge')
    } finally {
      setIsConnecting(false)
    }
  }, [client])

  const disconnect = useCallback(async () => {
    await client.disconnect()
    setDeviceInfo(null)
  }, [client])

  const productNames = useMemo(() => groups.map((group) => group.itemName), [groups])

  const colorNames = useMemo(() => {
    const group = groups.find((g) => g.itemName === selectedProductName)
    if (!group) return []
    return Array.from(new Set(group.variants.map((row) => row.colorName))).sort((a, b) =>
      a.localeCompare(b)
    )
  }, [groups, selectedProductName])

  const sizeVariants = useMemo(() => {
    const group = groups.find((g) => g.itemName === selectedProductName)
    if (!group) return []
    return group.variants
      .filter((row) => row.colorName === selectedColorName)
      .sort((a, b) => a.sizeName.localeCompare(b.sizeName))
  }, [groups, selectedProductName, selectedColorName])

  const selectProduct = useCallback((name: string | null) => {
    setSelectedProductName(name)
    setSelectedColorName(null)
    setSelectedVariant(null)
    setBlocked(null)
    setEncodeError(null)
  }, [])

  const selectColor = useCallback((name: string | null) => {
    setSelectedColorName(name)
    setSelectedVariant(null)
    setBlocked(null)
    setEncodeError(null)
  }, [])

  const selectVariant = useCallback((variant: RfidMatrixRow | null) => {
    setSelectedVariant(variant)
    setBlocked(null)
    setEncodeError(null)
  }, [])

  const buildContext = useCallback(() => {
    if (!adminId || !scheme || !selectedVariant?.epcBase) return null
    return {
      adminId,
      userId: user?.id ?? null,
      scheme,
      client,
      productId: selectedVariant.productId,
      epcBase: selectedVariant.epcBase,
      sku: selectedVariant.sku,
      barcode: selectedVariant.barcode,
      productName: selectedVariant.itemName,
      colorName: selectedVariant.colorName,
      sizeName: selectedVariant.sizeName,
      deviceSerial: deviceInfo?.serial ?? null,
      rfPower: deviceInfo?.rfPower ?? null,
    }
  }, [adminId, client, deviceInfo, scheme, selectedVariant, user?.id])

  const runEncode = useCallback(
    async (preRead: TagPreRead, supersedeRecord: RfidUnitRecord | null) => {
      const context = buildContext()
      if (!context) {
        setEncodeError('Select a complete variant with a valid EPC base first.')
        return
      }

      // The EPC physically erased is always recorded, including the factory one.
      const result = await encodeTag({
        context,
        preRead,
        supersedeRecord,
        overwroteEpc: preRead.epc,
      })

      setLastResult(result)
      if (result.outcome === 'quarantined') setEncodeError(result.error)
      await refreshStalePending()
    },
    [buildContext, refreshStalePending]
  )

  /** Pre-read, classify, and either write or stop. Never writes without a fresh read. */
  const startEncode = useCallback(async () => {
    if (!adminId || !scheme) return
    setEncodeError(null)
    setBlocked(null)
    setLastResult(null)
    setIsEncoding(true)

    try {
      const preRead = await preReadTag(client)
      setLastPreRead(preRead)

      const classification = await classifyTag({ adminId, scheme, preRead })
      if (classification.outcome === 'blocked') {
        setBlocked({ classification })
        return
      }

      await runEncode(preRead, null)
    } catch (err) {
      setEncodeError(err instanceof Error ? err.message : 'Encoding failed')
    } finally {
      setIsEncoding(false)
    }
  }, [adminId, client, runEncode, scheme])

  /** Only reachable from an explicit per-tag confirmation. */
  const confirmOverwrite = useCallback(async () => {
    const classification = blocked?.classification
    if (!classification || !classification.canOverwrite) return

    setIsEncoding(true)
    setEncodeError(null)
    try {
      await runEncode(classification.preRead, classification.existingRecord)
      setBlocked(null)
    } catch (err) {
      setEncodeError(err instanceof Error ? err.message : 'Overwrite failed')
    } finally {
      setIsEncoding(false)
    }
  }, [blocked, runEncode])

  const cancelOverwrite = useCallback(() => setBlocked(null), [])

  const isRfPowerHigh =
    deviceInfo?.rfPower !== null &&
    deviceInfo?.rfPower !== undefined &&
    deviceInfo.rfPower > RF_POWER_WARNING_THRESHOLD

  const isWorkModeUnverified =
    deviceInfo !== null && deviceInfo.workMode !== null && deviceInfo.workMode !== 0

  const getFrameLog = useCallback((): EncoderFrameLogEntry[] => {
    return client.getFrameLog?.() ?? []
  }, [client])

  const canEncode =
    status === 'connected' &&
    !isEncoding &&
    !!selectedVariant?.epcBase &&
    !!scheme &&
    !blocked

  return {
    adminId,
    scheme,
    isConfigLoading,
    configError,
    status,
    deviceInfo,
    connectionError,
    isConnecting,
    isRfPowerHigh,
    isWorkModeUnverified,
    getFrameLog,
    connect,
    disconnect,
    productNames,
    colorNames,
    sizeVariants,
    selectedProductName,
    selectedColorName,
    selectedVariant,
    selectProduct,
    selectColor,
    selectVariant,
    canEncode,
    isEncoding,
    encodeError,
    blocked: blocked?.classification ?? null,
    lastResult,
    lastPreRead,
    stalePending,
    startEncode,
    confirmOverwrite,
    cancelOverwrite,
  }
}
